import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Loader2, Search } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Input } from '../ui/Input';

const fmt = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

type SortKey = 'date_desc' | 'date_asc' | 'amount_desc' | 'amount_asc';

// Page dédiée à l'historique complet des factures liées à un contact CRM — extraite de la fiche contact
// (qui n'affichait qu'une liste brute, sans recherche ni tri) sur le même modèle que
// CRMContactDemarchesPage.tsx. Le bouton retour ramène à la fiche contact d'origine.
export const CRMContactFacturesPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [contact, setContact] = React.useState<any | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [q, setQ] = React.useState('');
    const [sort, setSort] = React.useState<SortKey>('date_desc');

    React.useEffect(() => {
        if (!id) return;
        moduleApi.getCRMContact(id).then((c: any) => setContact(c)).catch(() => setContact(null)).finally(() => setLoading(false));
    }, [id]);

    const contactLabel = contact
        ? (contact.type === 'PROFESSIONNEL' ? contact.company_name : `${contact.first_name || ''} ${contact.last_name || ''}`.trim())
        : '';

    const factureDate = (f: any) => f.due_date || f.created_at;
    const factureAmount = (f: any) => Number(f.total_ttc ?? f.amount ?? 0);

    const factures = React.useMemo(() => {
        const list = Array.isArray(contact?.factures) ? contact.factures : [];
        const query = q.trim().toLowerCase();
        const filtered = query ? list.filter((f: any) => String(f.invoice_number || '').toLowerCase().includes(query) || String(f.customer_name || '').toLowerCase().includes(query)) : list;
        const sorted = [...filtered].sort((a: any, b: any) => {
            if (sort === 'amount_desc') return factureAmount(b) - factureAmount(a);
            if (sort === 'amount_asc') return factureAmount(a) - factureAmount(b);
            const da = new Date(factureDate(a)).getTime(), db = new Date(factureDate(b)).getTime();
            return sort === 'date_asc' ? da - db : db - da;
        });
        return sorted;
    }, [contact, q, sort]);

    return (
        <div className="space-y-6">
            <header className="flex items-center gap-3">
                <button onClick={() => navigate(`/crm/contacts/${id}`)} className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all">
                    <ArrowLeft size={18} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-content flex items-center gap-2"><FileText size={22} /> Factures liées</h1>
                    <p className="text-content-muted mt-1">{contactLabel || '...'}</p>
                </div>
            </header>

            <div className="flex items-center gap-2 max-w-2xl">
                <div className="relative flex-1">
                    <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un numéro de facture, un client..." className="pl-8" />
                </div>
                <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}
                    className="bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500">
                    <option value="date_desc">Date (récent → ancien)</option>
                    <option value="date_asc">Date (ancien → récent)</option>
                    <option value="amount_desc">Montant (élevé → faible)</option>
                    <option value="amount_asc">Montant (faible → élevé)</option>
                </select>
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={28} /></div>
            ) : factures.length === 0 ? (
                <p className="text-center text-content-muted py-12 bg-surface rounded-2xl border border-dashed border-border max-w-2xl">Aucune facture liée.</p>
            ) : (
                <div className="space-y-3 max-w-2xl">
                    {factures.map((facture: any) => (
                        <div key={facture.id} className="bg-surface rounded-2xl border border-border p-4 flex items-center justify-between">
                            <div>
                                <p className="text-content font-bold">{facture.invoice_number || 'Facture'}</p>
                                <p className="text-xs text-content-muted mt-1">{facture.customer_name || 'Client'} • {fmtDate(factureDate(facture))}</p>
                            </div>
                            <div className="text-sm font-bold text-content">{fmt(factureAmount(facture))}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CRMContactFacturesPage;
